import React, { useEffect, useState } from 'react';
import { X, Briefcase, Calendar, BadgeDollarSign, CheckCircle2, Play, Edit, Trash2, ChevronDown } from 'lucide-react';
import './ViewProjectModal.css';
import './AddProjectModal.css';
import api from '../../../services/api';

const parseValueAndCurrency = (valueStr) => {
    if (!valueStr) return { currency: 'AED', amount: '' };
    const str = String(valueStr).trim();
    const match = str.match(/^(AED|USD|EUR|SAR|IND|INR)\s*(.*)$/i);
    if (match) {
        return {
            currency: match[1].toUpperCase() === 'INR' ? 'IND' : match[1].toUpperCase(),
            amount: match[2].trim()
        };
    }
    return { currency: 'AED', amount: str };
};

const formatIndian = (numStr) => {
    if (!numStr) return '';
    let lastThree = numStr.substring(numStr.length - 3);
    let otherDigits = numStr.substring(0, numStr.length - 3);
    if (otherDigits !== '') {
        lastThree = ',' + lastThree;
    }
    return otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
};

const formatInternational = (numStr) => {
    if (!numStr) return '';
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatValuation = (value, curr) => {
    if (value === undefined || value === null) return '';
    const rawVal = String(value).replace(/[^0-9.]/g, '');
    const dotIndex = rawVal.indexOf('.');
    let integerPart = rawVal;
    let decimalPart = '';
    if (dotIndex !== -1) {
        integerPart = rawVal.slice(0, dotIndex);
        decimalPart = rawVal.slice(dotIndex + 1);
    }

    let formattedInteger = '';
    if (curr === 'IND') {
        formattedInteger = formatIndian(integerPart);
    } else {
        formattedInteger = formatInternational(integerPart);
    }

    let result = formattedInteger;
    if (dotIndex !== -1) {
        result += '.' + decimalPart;
    }
    return result;
};

const ViewProjectModal = ({ project: initialProject, onClose, isManager, onEditSuccess, onDelete, initialMode = 'view' }) => {
    const [project, setProject] = useState(initialProject);
    const [isEditing, setIsEditing] = useState(initialMode === 'edit');
    const [submitting, setSubmitting] = useState(false);
    const [apiError, setApiError] = useState('');
    const [formErrors, setFormErrors] = useState({});

    const validateDate = (dateStr) => {
        if (!dateStr) return true;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return false;
        const year = d.getFullYear();
        return year >= 1900 && year <= new Date().getFullYear();
    };

    const getInitialStatus = (p) => {
        if (!p?.status) return '';
        const s = p.status.toUpperCase();
        if (s === 'COMPLETED' || s === 'COMPLETE') return 'Completed';
        if (s === 'INPROGRESS' || s === 'IN_PROGRESS' || s === 'IN PROGRESS') return 'In Progress';
        return p.status;
    };

    const parsedInit = parseValueAndCurrency(project?.value);
    const [form, setForm] = useState({
        title: project?.title || '',
        client: project?.client || '',
        status: getInitialStatus(project),
        currency: parsedInit.currency,
        amount: parsedInit.amount,
        completed_date: project?.completed_date || '',
        description: project?.description || '',
    });

    useEffect(() => {
        setProject(initialProject);
        if (initialProject) {
            const parsed = parseValueAndCurrency(initialProject.value);
            setForm({
                title: initialProject.title || '',
                client: initialProject.client || '',
                status: getInitialStatus(initialProject),
                currency: parsed.currency,
                amount: parsed.amount,
                completed_date: initialProject.completed_date || '',
                description: initialProject.description || '',
            });
        }
    }, [initialProject]);

    useEffect(() => {
        setIsEditing(initialMode === 'edit');
    }, [initialMode]);

    useEffect(() => {
        // Lock body scrolling when modal opens
        document.body.style.overflow = 'hidden';
        return () => {
            // Restore body scrolling on unmount
            document.body.style.overflow = '';
        };
    }, []);

    if (!project) return null;

    const toTitleCase = (str) => {
        if (!str) return 'N/A';
        return str.split(' ').map(word => {
            const lower = word.toLowerCase();
            if (lower === 'uae') return 'UAE';
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join(' ');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => {
            const nextForm = { ...prev, [name]: value };
            if (name === 'status' && value !== 'Completed') {
                nextForm.completed_date = '';
            }
            if (name === 'amount') {
                let clean = value.replace(/[^0-9.,bmkBMK]/g, '');
                const parts = clean.split('.');
                if (parts.length > 2) {
                    clean = parts[0] + '.' + parts.slice(1).join('');
                }
                const hasSuffix = /[bmkBMK]$/.test(clean);
                const suffix = hasSuffix ? clean.slice(-1) : '';
                const numberPart = hasSuffix ? clean.slice(0, -1) : clean;

                const raw = numberPart.replace(/,/g, '');
                nextForm.amount = formatValuation(raw, prev.currency) + suffix.toUpperCase();
            }
            if (name === 'currency') {
                const hasSuffix = /[bmkBMK]$/.test(prev.amount);
                const suffix = hasSuffix ? prev.amount.slice(-1) : '';
                const numberPart = hasSuffix ? prev.amount.slice(0, -1) : prev.amount;
                const raw = numberPart.replace(/,/g, '');
                nextForm.amount = formatValuation(raw, value) + suffix.toUpperCase();
            }
            return nextForm;
        });
        if (apiError) setApiError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = {};
        if (!form.title.trim()) errors.title = 'Proposal title is required.';
        if (!form.client.trim()) errors.client = 'Client name is required.';
        if (form.status === 'Completed' && !form.completed_date) {
            errors.completed_date = 'Completed date is required for Completed status.';
        }
        if (form.completed_date && !validateDate(form.completed_date)) {
            errors.completed_date = 'Please enter a valid date.';
        }
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        setFormErrors({});
        setSubmitting(true);
        setApiError('');

        const projectId = project.project_id || project.id || project._id;
        const combinedValue = `${form.currency} ${form.amount}`.trim();

        try {
            const payload = {
                title: form.title.trim(),
                client: form.client.trim() || 'Not Specified',
                status: form.status || 'In Progress',
                value: combinedValue || 'Not Specified',
                completed_date: form.completed_date,
                description: form.description.trim(),
            };
            const response = await api.put(`/experience/${projectId}`, payload);

            if (response.status === 200 || response.status === 201) {
                const updated = {
                    ...project,
                    title: form.title,
                    client: form.client,
                    status: form.status || 'In Progress',
                    value: combinedValue,
                    completed_date: form.completed_date,
                    description: form.description,
                };
                setProject(updated);
                setIsEditing(false);
                if (onEditSuccess) {
                    onEditSuccess(updated, response.data?.message || 'Project updated successfully');
                }
            } else {
                setApiError(response.data?.detail || 'Failed to update project. Please try again.');
            }
        } catch (err) {
            console.error('Update project error:', err);
            setApiError('Network error. Please check your connection.');
        } finally {
            setSubmitting(false);
        }
    };

    const isCompleted = project.status === 'Completed' || project.status === 'COMPLETED';

    // RENDER EDITING FORM INSIDE THE SAME CARD
    if (isEditing) {
        return (
            <div className="vpm-overlay" onClick={onClose}>
                <div className="vpm-container animate-vpm-scale" onClick={(e) => e.stopPropagation()}>
                    {/* Header Section */}
                    <div className="vpm-header">
                        <div className="vpm-header-left">
                            <div className="vpm-icon-badge">
                                <Edit size={18} />
                            </div>
                            <div className="vpm-header-text">
                                <h3>Edit Company Project</h3>
                                <p>Update details and status configurations</p>
                            </div>
                        </div>
                        <button type="button" className="vpm-close-btn" onClick={onClose} aria-label="Close modal">
                            <X size={18} />
                        </button>
                    </div>

                    <form className="apm-form" onSubmit={handleSubmit} style={{ margin: 0 }}>
                        <div className="vpm-body">
                            <div className="apm-form-grid">
                                {/* Proposal Title */}
                                <div className="apm-form-field">
                                    <label className="apm-label">Proposal title <span style={{color:'#dc2626'}}>*</span></label>
                                    <input
                                        className="apm-input"
                                        type="text"
                                        name="title"
                                        placeholder="Enter project title..."
                                        value={form.title}
                                        onChange={handleChange}
                                        autoFocus
                                        required
                                    />
                                    {formErrors.title && <span style={{fontSize:'11px',color:'#dc2626'}}>{formErrors.title}</span>}
                                </div>

                                {/* Client & Project Value Row */}
                                <div className="apm-form-row">
                                    <div className="apm-form-field">
                                        <label className="apm-label">Client <span style={{color:'#dc2626'}}>*</span></label>
                                        <input
                                            className="apm-input"
                                            type="text"
                                            name="client"
                                            placeholder="Client name..."
                                            value={form.client}
                                            onChange={handleChange}
                                            required
                                        />
                                        {formErrors.client && <span style={{fontSize:'11px',color:'#dc2626'}}>{formErrors.client}</span>}
                                    </div>

                                    <div className="apm-form-field">
                                        <label className="apm-label">Project value</label>
                                        <div className="valuation-composite-v3" style={{ marginTop: 0 }}>
                                            <div className="currency-box-v3">
                                                {/*
                                                <select name="currency" value={form.currency} onChange={handleChange}>
                                                    <option value="AED">AED</option>
                                                    <option value="USD">USD</option>
                                                    <option value="EUR">EUR</option>
                                                    <option value="SAR">SAR</option>
                                                    <option value="IND">IND</option>
                                                </select>
                                                <ChevronDown size={12} className="select-arrow-v3" />
                                                */}
                                                <span style={{ fontWeight: 600, fontSize: '13px', color: '#475569', padding: '0 8px' }}>AED</span>
                                            </div>
                                            <input
                                                type="text"
                                                name="amount"
                                                placeholder="e.g. 300,000"
                                                value={form.amount}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Status & Completed Date Row */}
                                <div className="apm-form-row">
                                    <div className="apm-form-field">
                                        <label className="apm-label">Status</label>
                                        <select
                                            className="apm-input apm-select"
                                            name="status"
                                            value={form.status}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select status</option>
                                            <option value="Completed">Completed</option>
                                            <option value="In Progress">In Progress</option>
                                        </select>
                                    </div>

                                    <div className="apm-form-field">
                                        <label className="apm-label">Completed date {form.status === 'Completed' && <span style={{color:'#dc2626'}}>*</span>}</label>
                                        <input
                                            className={`apm-input apm-date-input${formErrors.completed_date ? ' apm-input-error' : ''}`}
                                            type="date"
                                            name="completed_date"
                                            value={form.completed_date}
                                            onChange={handleChange}
                                            min="1900-01-01"
                                            max={new Date().toISOString().split('T')[0]}
                                            disabled={form.status !== 'Completed'}
                                        />
                                        {formErrors.completed_date && <span style={{fontSize:'11px',color:'#dc2626'}}>{formErrors.completed_date}</span>}
                                    </div>
                                </div>

                                <div className="apm-form-field">
                                    <label className="apm-label">Description</label>
                                    <textarea
                                        className="apm-input apm-textarea"
                                        name="description"
                                        placeholder="Brief project description..."
                                        value={form.description}
                                        onChange={handleChange}
                                        rows={3}
                                        style={{ fontFamily: 'inherit', fontWeight: 400 }}
                                    />
                                </div>
                            </div>

                            {apiError && (
                                <div className="apm-api-error">{apiError}</div>
                            )}
                        </div>

                        {/* Footer Section */}
                        <div className="apm-footer" style={{ padding: '16px 24px' }}>
                            <button
                                type="button"
                                className="apm-btn-cancel"
                                onClick={() => {
                                    if (initialMode === 'edit') {
                                        onClose();
                                    } else {
                                        setIsEditing(false);
                                    }
                                }}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="apm-btn-add" disabled={submitting}>
                                <span>{submitting ? 'Saving...' : 'Save Changes'}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    // RENDER VIEW DETAILS
    return (
        <div className="vpm-overlay" onClick={onClose}>
            <div className="vpm-container animate-vpm-scale" onClick={(e) => e.stopPropagation()}>
                {/* Header Section */}
                <div className="vpm-header">
                    <div className="vpm-header-left">
                        <div className="vpm-icon-badge">
                            <Briefcase size={18} />
                        </div>
                        <div className="vpm-header-text">
                            <h3>Project Details</h3>
                            <p>Detailed project overview and current status</p>
                        </div>
                    </div>
                    <div className="vpm-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isManager && (
                            <>
                                <button className="vpm-edit-btn" onClick={() => setIsEditing(true)} title="Edit Project">
                                    <Edit size={14} />
                                    <span>Edit</span>
                                </button>
                                <button className="vpm-delete-btn" onClick={() => onDelete(project)} title="Delete Project">
                                    <Trash2 size={14} />
                                    <span>Delete</span>
                                </button>
                                <div style={{ width: '1px', height: '20px', background: '#E2E8F0', margin: '0 4px' }} />
                            </>
                        )}
                        <button type="button" className="vpm-close-btn" onClick={onClose} aria-label="Close modal">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body Content */}
                <div className="vpm-body">
                    {/* Hero Section */}
                    <div className="vpm-hero-section">
                        <div className="vpm-hero-left">
                            <h1 className="vpm-proposal-title">{toTitleCase(project.title)}</h1>
                            <p className="vpm-client-name">{toTitleCase(project.client) || 'Not Specified'}</p>
                        </div>
                        <div className="vpm-hero-right">
                            <div className={`vpm-status-badge ${isCompleted ? 'vpm-status-completed' : 'vpm-status-progress'}`}>
                                {isCompleted ? (
                                    <>
                                        {/* <CheckCircle2 size={13} strokeWidth={3} className="vpm-status-icon" /> */}
                                        <span>Completed</span>
                                    </>
                                ) : (
                                    <>
                                        {/* <Play size={13} fill="currentColor" className="vpm-status-icon" /> */}
                                        <span>In Progress</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Metric / Stats Cards Section */}
                    <div className="vpm-stats-grid">
                        <div className="vpm-stat-card">
                            <div className="vpm-stat-header">
                                <span style={{
                                    fontSize: '11px', fontWeight: 700, color: '#ef4444',
                                    background: '#fef2f2', border: '1px solid #fca5a5',
                                    borderRadius: '4px', padding: '1px 5px', letterSpacing: '0.03em'
                                }}>AED</span>
                                <span>Project value</span>
                            </div>
                            <div className="vpm-stat-value">{project.value || 'Not Specified'}</div>
                        </div>

                        <div className="vpm-stat-card">
                            <div className="vpm-stat-header">
                                <Calendar size={14} className="vpm-stat-icon-date" />
                                <span>Completion date</span>
                            </div>
                            <div className="vpm-stat-value">{project.completed_date || 'N/A'}</div>
                        </div>
                    </div>

                    {/* Description Section */}
                    <div className="vpm-description-section">
                        <h4 className="vpm-section-label">Description</h4>
                        <div className="vpm-description-card">
                            <p>{project.description || 'No description provided.'}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewProjectModal;
